import React from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  Icon,
  Table,
  Separator,
  List,
} from '@chakra-ui/react';
import { Thead, Tbody, Tr, Th, Td } from '@/components/ui/chakra-v2-compat';
import { FaArrowLeft } from 'react-icons/fa';
import Footer from './Footer';
import SEO from './SEO.jsx';

const PoliticaPrivacidad = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <SEO
        title="Política de Privacidad"
        description="Política de privacidad de Cadencya. Conoce cómo protegemos y tratamos tus datos personales."
        path="/politica-privacidad"
      />
      {/* Header */}
      <Box bg="purple.600" color="white" py={6}>
        <Container maxW="7xl">
          <Button
            variant="ghost"
            color="white"
            _hover={{ bg: 'purple.700' }}
            mb={4}
            asChild><RouterLink to="/"><Icon asChild><FaArrowLeft /></Icon>Volver al inicio
                                    </RouterLink></Button>
          <Heading size="2xl" mb={4}>
            Política de Privacidad
          </Heading>
          <Text fontSize="md">
            Última actualización: 25 de febrero de 2026
          </Text>
        </Container>
      </Box>

      {/* Contenido principal */}
      <Box flex="1" bg={bgColor} py={16}>
        <Container maxW="5xl">
          <Box bg={cardBg} p={{ base: 6, md: 10 }} rounded="2xl" shadow="lg">
            <VStack gap={8} align="stretch">
              {/* 1. Responsable del tratamiento */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  1. Responsable del Tratamiento de Datos
                </Heading>
                <Text color="gray.700" mb={3}>
                  De conformidad con el Reglamento General de Protección de Datos (RGPD - UE 2016/679) y la Ley Orgánica 3/2018 de Protección de Datos Personales y garantía de los derechos digitales (LOPDGDD), le informamos:
                </Text>
                <Text color="gray.700">
                  <strong>Responsable:</strong> Cadencya<br />
                  <strong>Email de contacto:</strong> info@cadencya.com
                </Text>
              </Box>

              <Separator />

              {/* 2. Datos que recopilamos */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  2. Datos Personales que Recopilamos
                </Heading>
                <Text color="gray.700" mb={4}>
                  Recopilamos y tratamos las siguientes categorías de datos personales:
                </Text>
                
                <Heading size="md" color="gray.800" mb={3}>
                  2.1. Datos de registro y perfil
                </Heading>
                <List.Root as='ul' gap={2} color="gray.700" ml={6} mb={4}>
                  <List.Item><strong>Para bandas:</strong> Nombre de la banda, CIF/NIF, email, teléfono, dirección, tipo de agrupación, número de músicos, representante legal, fotografías, vídeos, repertorio, documentos de verificación.</List.Item>
                  <List.Item><strong>Para cofradías:</strong> Nombre de la hermandad, CIF, email, teléfono, dirección, persona de contacto, fotografías, documentos de verificación.</List.Item>
                </List.Root>

                <Heading size="md" color="gray.800" mb={3}>
                  2.2. Datos de uso
                </Heading>
                <List.Root as='ul' gap={2} color="gray.700" ml={6} mb={4}>
                  <List.Item>Información sobre eventos creados, acuerdos propuestos y aceptados</List.Item>
                  <List.Item>Mensajes y comunicaciones entre usuarios</List.Item>
                  <List.Item>Calendario de disponibilidad</List.Item>
                  <List.Item>Reseñas y valoraciones</List.Item>
                  <List.Item>Historial de búsquedas y navegación</List.Item>
                </List.Root>

                <Heading size="md" color="gray.800" mb={3}>
                  2.3. Datos técnicos
                </Heading>
                <List.Root as='ul' gap={2} color="gray.700" ml={6}>
                  <List.Item>Dirección IP, tipo de navegador, sistema operativo</List.Item>
                  <List.Item>Cookies y tecnologías similares (ver nuestra{' '}
                    <Text color="purple.600" fontWeight="600" asChild><RouterLink to="/politica-cookies">Política de Cookies
                                          </RouterLink></Text>)
                  </List.Item>
                  <List.Item>Logs de acceso y uso de la plataforma</List.Item>
                  <List.Item>Geolocalización (aproximada, basada en la ubicación proporcionada)</List.Item>
                </List.Root>
              </Box>

              <Separator />

              {/* 3. Finalidad y base legal */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  3. Finalidad y Base Legal del Tratamiento
                </Heading>
                <Box overflowX="auto">
                  <Table.Root variant="simple" size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>Finalidad</Table.ColumnHeader>
                        <Table.ColumnHeader>Base Legal (RGPD)</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      <Table.Row>
                        <Table.Cell>Gestión de registro y acceso a la plataforma</Table.Cell>
                        <Table.Cell>Ejecución de contrato (Art. 6.1.b)</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell>Facilitar la conexión entre bandas y cofradías</Table.Cell>
                        <Table.Cell>Ejecución de contrato (Art. 6.1.b)</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell>Gestión de eventos, acuerdos y mensajería</Table.Cell>
                        <Table.Cell>Ejecución de contrato (Art. 6.1.b)</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell>Verificación de identidad y prevención de fraude</Table.Cell>
                        <Table.Cell>Interés legítimo (Art. 6.1.f)</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell>Envío de notificaciones sobre la plataforma</Table.Cell>
                        <Table.Cell>Ejecución de contrato (Art. 6.1.b)</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell>Envío de comunicaciones comerciales</Table.Cell>
                        <Table.Cell>Consentimiento (Art. 6.1.a)</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell>Mejora de servicios y análisis estadístico</Table.Cell>
                        <Table.Cell>Interés legítimo (Art. 6.1.f)</Table.Cell>
                      </Table.Row>
                      <Table.Row>
                        <Table.Cell>Cumplimiento de obligaciones legales</Table.Cell>
                        <Table.Cell>Obligación legal (Art. 6.1.c)</Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  </Table.Root>
                </Box>
              </Box>

              <Separator />

              {/* 4. Destinatarios */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  4. Destinatarios de los Datos
                </Heading>
                <Text color="gray.700" mb={3}>
                  Sus datos personales podrán ser comunicados a:
                </Text>
                <List.Root as='ul' gap={2} color="gray.700" ml={6} mb={4}>
                  <List.Item><strong>Otros usuarios de la plataforma:</strong> Según la funcionalidad (por ejemplo, cuando una cofradía contacta con una banda, ambos pueden ver los datos de perfil del otro).</List.Item>
                  <List.Item><strong>Proveedores de servicios tecnológicos:</strong> Hosting, email transaccional (SendGrid), monitorización de errores (GlitchTip, alojado en servidor propio dentro de la UE), análisis web (Google Analytics, con consentimiento previo), pasarelas de pago (cuando aplique).</List.Item>
                  <List.Item><strong>Proveedores de inteligencia artificial:</strong> OpenAI, para funcionalidades de procesamiento de texto dentro de la plataforma. Los datos enviados se limitan al contenido estrictamente necesario para la funcionalidad solicitada.</List.Item>
                  <List.Item><strong>Autoridades y organismos públicos:</strong> Cuando sea legalmente requerido.</List.Item>
                </List.Root>
                <Text color="gray.700" mb={3}>
                  Todos nuestros proveedores están obligados contractualmente a cumplir con el RGPD y actúan como encargados del tratamiento.
                </Text>
                <Text color="gray.700">
                  <strong>Transferencias internacionales:</strong> Algunos de nuestros proveedores (OpenAI, SendGrid, Google Analytics) pueden estar ubicados fuera del Espacio Económico Europeo, principalmente en Estados Unidos. En estos casos, garantizamos que se aplican las garantías adecuadas, como las Cláusulas Contractuales Tipo (SCCs) aprobadas por la Comisión Europea o el Marco de Privacidad de Datos UE-EE.UU. (EU-US Data Privacy Framework).
                </Text>
              </Box>

              <Separator />

              {/* 5. Conservación */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  5. Plazo de Conservación
                </Heading>
                <Text color="gray.700" mb={3}>
                  Conservaremos sus datos personales durante los siguientes plazos:
                </Text>
                <List.Root as='ul' gap={2} color="gray.700" ml={6} mb={4}>
                  <List.Item><strong>Datos de perfil activo:</strong> Mientras mantenga su cuenta activa.</List.Item>
                  <List.Item><strong>Tras cancelación de cuenta:</strong> Hasta 5 años para cumplir con obligaciones legales (fiscales, contractuales, etc.).</List.Item>
                  <List.Item><strong>Comunicaciones y mensajes:</strong> Durante la vida de la cuenta y hasta 5 años tras su cancelación.</List.Item>
                  <List.Item><strong>Datos de facturación:</strong> 10 años (obligación fiscal).</List.Item>
                  <List.Item><strong>Datos de marketing:</strong> Hasta que retire su consentimiento.</List.Item>
                  <List.Item><strong>Logs técnicos:</strong> Máximo 2 años.</List.Item>
                </List.Root>
                <Text color="gray.700">
                  Transcurridos estos plazos, los datos serán eliminados de forma segura o anonimizados para fines estadísticos.
                </Text>
              </Box>

              <Separator />

              {/* 6. Derechos del usuario */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  6. Sus Derechos (RGPD)
                </Heading>
                <Text color="gray.700" mb={4}>
                  Usted tiene derecho a:
                </Text>
                <List.Root as='ul' gap={2} color="gray.700" ml={6} mb={4}>
                  <List.Item><strong>Acceso:</strong> Obtener confirmación sobre si estamos tratando sus datos y acceder a ellos.</List.Item>
                  <List.Item><strong>Rectificación:</strong> Corregir datos inexactos o incompletos.</List.Item>
                  <List.Item><strong>Supresión ("derecho al olvido"):</strong> Solicitar la eliminación de sus datos cuando ya no sean necesarios.</List.Item>
                  <List.Item><strong>Limitación del tratamiento:</strong> Solicitar que limitemos el tratamiento en determinadas circunstancias.</List.Item>
                  <List.Item><strong>Portabilidad:</strong> Recibir sus datos en formato estructurado y transmitirlos a otro responsable.</List.Item>
                  <List.Item><strong>Oposición:</strong> Oponerse al tratamiento basado en interés legítimo o para fines de marketing.</List.Item>
                  <List.Item><strong>No ser objeto de decisiones automatizadas:</strong> Incluido el perfilado.</List.Item>
                  <List.Item><strong>Retirar el consentimiento:</strong> En cualquier momento, sin que afecte a la licitud del tratamiento previo.</List.Item>
                </List.Root>
                <Text color="gray.700" mb={3}>
                  <strong>Cómo ejercer sus derechos:</strong>
                </Text>
                <List.Root as='ul' gap={2} color="gray.700" ml={6} mb={4}>
                  <List.Item>Email: info@cadencya.com</List.Item>
                  <List.Item>Desde su perfil: Configuración → Privacidad y datos</List.Item>
                </List.Root>
                <Text color="gray.700" mb={3}>
                  Responderemos a su solicitud en el plazo máximo de 1 mes.
                </Text>
                <Text color="gray.700">
                  <strong>Derecho a reclamar:</strong> Si considera que sus derechos han sido vulnerados, puede presentar una reclamación ante la Agencia Española de Protección de Datos (AEPD) en{' '}
                  <Text color="purple.600" fontWeight="600" asChild><a href="https://www.aepd.es" target="_blank">www.aepd.es
                                      </a></Text>
                </Text>
              </Box>

              <Separator />

              {/* 7. Seguridad */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  7. Medidas de Seguridad
                </Heading>
                <Text color="gray.700" mb={3}>
                  Hemos implementado medidas técnicas y organizativas apropiadas para proteger sus datos personales contra:
                </Text>
                <List.Root as='ul' gap={2} color="gray.700" ml={6} mb={4}>
                  <List.Item>Destrucción accidental o ilícita</List.Item>
                  <List.Item>Pérdida accidental</List.Item>
                  <List.Item>Alteración, difusión o acceso no autorizados</List.Item>
                </List.Root>
                <Text color="gray.700" mb={3}>
                  Entre nuestras medidas de seguridad se incluyen:
                </Text>
                <List.Root as='ul' gap={2} color="gray.700" ml={6}>
                  <List.Item>Cifrado SSL/TLS para todas las transmisiones de datos</List.Item>
                  <List.Item>Cifrado de contraseñas mediante algoritmos seguros</List.Item>
                  <List.Item>Control de acceso basado en roles</List.Item>
                  <List.Item>Auditorías de seguridad periódicas</List.Item>
                  <List.Item>Copias de seguridad regulares</List.Item>
                  <List.Item>Formación del personal en protección de datos</List.Item>
                </List.Root>
              </Box>

              <Separator />

              {/* 8. Menores de edad */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  8. Menores de Edad
                </Heading>
                <Text color="gray.700">
                  Cadencya está dirigida a usuarios mayores de 18 años. No recopilamos intencionadamente datos de menores de 14 años. Si detectamos que un menor de 14 años ha proporcionado datos sin consentimiento parental, eliminaremos inmediatamente dicha información. Si tiene conocimiento de que un menor ha proporcionado datos, contáctenos en soporte@cadencya.com.
                </Text>
              </Box>

              <Separator />

              {/* 9. Modificaciones */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  9. Modificaciones de la Política
                </Heading>
                <Text color="gray.700">
                  Nos reservamos el derecho de modificar esta Política de Privacidad para adaptarla a cambios legislativos o en nuestros servicios. Le notificaremos cualquier cambio significativo por email o mediante aviso destacado en la plataforma. La fecha de última actualización se indica al inicio de este documento.
                </Text>
              </Box>

              <Separator />

              {/* 10. Contacto */}
              <Box>
                <Heading size="lg" color="purple.600" mb={4}>
                  10. Contacto
                </Heading>
                <Text color="gray.700" mb={2}>
                  Para cualquier consulta sobre privacidad y protección de datos:
                </Text>
                <Text color="gray.700">
                  <strong>Email:</strong> info@cadencya.com
                </Text>
              </Box>

              {/* Nota final */}
              <Box bg="purple.50" p={6} rounded="xl" mt={8}>
                <Text fontSize="sm" color="gray.700" textAlign="center">
                  Esta Política de Privacidad cumple con el RGPD (UE 2016/679) y la LOPDGDD (España).
                  Para más información sobre sus derechos, visite{' '}
                  <Text color="purple.600" fontWeight="600" asChild><a href="https://www.aepd.es" target="_blank">www.aepd.es
                                      </a></Text>
                </Text>
              </Box>
            </VStack>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};

export default PoliticaPrivacidad;
